import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  EngDiagQuestionResponse,
  EngDiagSkipReason,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"

/**
 * The student's side of an English Skill Builder - the remediation assigned when a diagnostic
 * found a gap. Everything is scoped server-side to the authenticated student, so a key from the
 * client is never trusted on its own.
 *
 * Its own endpoints rather than /studentEngDiag/*: a Skill Builder is a flat list of questions
 * for one skill, with no topic blocks, so there is no completeTopic here and the state it returns
 * is a different shape.
 *
 * The tutor's endpoints (previewing, assigning) are /engDiag/* and are not reachable from here.
 */
const ENDPOINTS = {
  getState: "studentEngSkillBuilder/getSkillBuilderState",
  getQuestion: "studentEngSkillBuilder/getQuestion",
  submitAnswer: "studentEngSkillBuilder/submitAnswer",
  skipQuestion: "studentEngSkillBuilder/skipQuestion",
  getAudio: "studentEngSkillBuilder/getAudio",
} as const

/** Either key identifies it: the activity is what the lesson plan knows about. */
export type EngSkillBuilderRef = {
  activity_key?: string
  sb_session_key?: string
}

/**
 * Everything the player needs to draw itself. Safe to fetch at any point - this is how a Skill
 * Builder resumes after the child closes the tab.
 *
 * NO YEAR LEVEL, deliberately, unlike the diagnostics state which carries it for the report. The
 * SKILL is here and is meant to be shown: naming what they are practising is the point of
 * remediation. The year they are being assessed at is not theirs to see.
 */
export type EngSkillBuilderState = {
  pk_sb_session_key: string
  flow_status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  skill_name: string | null
  cluster_name: string | null
  current_question_key: string | null
  question_count: number
  answered_count: number
  /** Answers sitting with a tutor. They count as done for progress, but not yet as right. */
  awaiting_marking_count: number
  unserved_question_count: number
  started_on: string | null
  completed_on: string | null
  error?: string
}

/**
 * What comes back after answering or skipping.
 *
 * `is_correct` IS returned, unlike the diagnostic. A Skill Builder is practice for a skill the
 * child has already been shown to be weak at, and practice without feedback is just more of the
 * thing that did not work. `null` means a tutor has to mark it - which is not the same as wrong,
 * and the player says so.
 */
export type EngSkillBuilderSubmitResponse = {
  status: 0 | 1
  error?: string
  is_correct?: boolean | null
  awaiting_marking?: boolean
  session_complete?: boolean
  next_question_key?: string | null
  question_count?: number
  answered_count?: number
  awaiting_marking_count?: number
  unserved_question_count?: number
}

export async function getEngSkillBuilderState(
  ref: EngSkillBuilderRef,
  signal?: AbortSignal,
): Promise<EngSkillBuilderState> {
  const response = await apiClient.post<EngSkillBuilderState>(
    BASE_URL + ENDPOINTS.getState,
    ref,
    { signal },
  )
  return response.data
}

/** The next question, or a named one. Omit the key and the server serves whatever is due. */
export async function getEngSkillBuilderQuestion(
  req: EngSkillBuilderRef & { sb_question_key?: string },
  signal?: AbortSignal,
): Promise<EngDiagQuestionResponse> {
  const response = await apiClient.post<EngDiagQuestionResponse>(
    BASE_URL + ENDPOINTS.getQuestion,
    req,
    { signal },
  )
  return response.data
}

/** Record an answer. The reply says how they did, and what comes next. */
export async function submitEngSkillBuilderAnswer(
  req: EngSkillBuilderRef & {
    sb_question_key: string
    response: EngDiagStudentAnswers
    duration_seconds?: number
  },
): Promise<EngSkillBuilderSubmitResponse> {
  const response = await apiClient.post<EngSkillBuilderSubmitResponse>(
    BASE_URL + ENDPOINTS.submitAnswer,
    req,
  )
  return response.data
}

/** "Skip for now". The question returns at the end, up to a server-side limit. */
export async function skipEngSkillBuilderQuestion(
  req: EngSkillBuilderRef & {
    sb_question_key: string
    skip_reason: EngDiagSkipReason
    duration_seconds?: number
  },
): Promise<EngSkillBuilderSubmitResponse> {
  const response = await apiClient.post<EngSkillBuilderSubmitResponse>(
    BASE_URL + ENDPOINTS.skipQuestion,
    req,
  )
  return response.data
}

/**
 * The bytes of one audio clip in this student's Skill Builder.
 *
 * Clips live in the database, not on a CDN, and the endpoint needs the student's token - so a
 * plain <audio src> cannot reach them. The caller turns this blob into an object URL.
 */
export async function fetchEngSkillBuilderAudio(
  audioId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiClient.post(
    BASE_URL + ENDPOINTS.getAudio,
    { audio_id: audioId },
    { responseType: "blob", signal },
  )
  return response.data as Blob
}
